import { ICONS, PERF_STYLES } from './constants';
// Date conversion
export const convertDateFormat = (dateStr) => {
    if (!dateStr)
        return '';
    if (dateStr.includes('T'))
        return dateStr.split('T')[0];
    if (dateStr.includes('/')) {
        const [month, day, year] = dateStr.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
};
// Holiday events
export const convertHolidaysToEvents = (holidays, currentClientId) => {
    if (!holidays?.length)
        return [];
    return holidays
        .filter(h => {
        if (h.status !== 'active')
            return false;
        const hClientId = h.client_id?._id || h.client_id;
        // If holiday has a client_id, show only if it matches currentClientId.
        if (hClientId) {
            return currentClientId ? String(hClientId) === String(currentClientId) : false;
        }
        return true;
    })
        .map(holiday => {
        const startDate = convertDateFormat(holiday.start_date);
        const endDateObj = new Date(convertDateFormat(holiday.end_date));
        endDateObj.setDate(endDateObj.getDate() + 1);
        return {
            id: `holiday-${holiday._id}`,
            title: holiday.name,
            start: startDate,
            end: endDateObj.toISOString().slice(0, 10),
            allDay: true,
            display: "block",
            order: -1,
            extendedProps: { type: "holiday", isHoliday: true }
        };
    });
};
export const convertToEvents = (data, group, selectedTab = 'all', targetMonth, showPerformance = true) => {
    if (!data)
        return [];
    const calendarData = ('all' in data || 'nba' in data || 'nfl' in data)
        ? data[selectedTab] || {}
        : data;
    const events = [];
    const createdRangeIds = new Set(); // ✅ avoid duplicates
    const toYMD = (d) => (d ? String(d).slice(0, 10) : null);
    const addDaysYMD = (ymd, days) => {
        const dt = new Date(`${ymd}T00:00:00.000Z`);
        dt.setUTCDate(dt.getUTCDate() + days);
        return dt.toISOString().slice(0, 10);
    };
    Object.entries(calendarData).forEach(([date, dailyData]) => {
        const displayDate = (group !== 'current' && targetMonth)
            ? `${targetMonth}-${date.split('-')[2]}`
            : date;
        const campaigns = dailyData?.campaignData || [];
        const st = selectedTab.toLowerCase();
        // ✅ Filter campaigns by league first (for both event creation and performance badges)
        const leagueFilteredCampaigns = st === 'all'
            ? campaigns
            : campaigns.filter(c => !c.league?.length || c.league.map(l => l.toLowerCase()).includes(st));
        leagueFilteredCampaigns.forEach((campaign) => {
            const typeStr = String(campaign.type || '').toLowerCase();
            const isProposal = typeStr === 'proposal';
            const isDraft = typeStr === 'draft';
            // Do not show historical drafts or proposals on the calendar
            if (group !== 'current' && (isProposal || isDraft)) {
                return;
            }
            // ✅ PROPOSAL => multi-day range event
            if (isProposal) {
                const start = toYMD(campaign.startDate) || displayDate;
                const endInclusive = toYMD(campaign.endDate) || start;
                const proposalId = campaign.id || campaign._id || campaign.name;
                const uniqueId = `${proposalId}`;
                if (createdRangeIds.has(uniqueId))
                    return;
                createdRangeIds.add(uniqueId);
                events.push({
                    id: uniqueId,
                    title: campaign.name,
                    start,
                    end: addDaysYMD(endInclusive, 1), // end exclusive
                    allDay: true,
                    display: "block",
                    order: 0,
                    editable: true,
                    extendedProps: {
                        ...campaign,
                        type: "proposal", // ✅ force
                        startDate: start, // ✅ normalize
                        endDate: endInclusive, // ✅ normalize
                        proposalId, // ✅ stable id for edit/update
                        group,
                        originalId: proposalId
                    }
                });
                return;
            }
            // ✅ Normal 1-day campaign (only render if EventContent supports it)
            if (isDraft) {
                events.push({
                    id: `${group}-${campaign.id || campaign.name}-${displayDate}`,
                    title: campaign.name,
                    start: displayDate,
                    allDay: true,
                    order: 1,
                    extendedProps: { ...campaign, group, originalId: campaign.id }
                });
            }
        });
        // ✅ Performance events - now using league-filtered campaigns
        const addPerfEvent = (level, filtered) => {
            if (filtered.length) {
                // ✅ Recalculate aggregate for filtered campaigns only
                const filteredAggregate = {
                    ...dailyData.aggregate,
                    high: level === 'high' ? filtered.reduce((sum, c) => sum + (c.revenue || 0), 0) : dailyData.aggregate.high,
                    low: level === 'low' ? filtered.reduce((sum, c) => sum + (c.revenue || 0), 0) : dailyData.aggregate.low,
                };
                events.push({
                    id: `${group}-perf-${level}-${displayDate}`,
                    title: '',
                    start: displayDate,
                    allDay: true,
                    order: 2,
                    extendedProps: { type: 'performance', level, group, campaigns: filtered, aggregate: filteredAggregate }
                });
            }
        };
        if (showPerformance) {
            addPerfEvent('high', leagueFilteredCampaigns.filter(c => c.performance?.toLowerCase() === 'high'));
            addPerfEvent('low', leagueFilteredCampaigns.filter(c => c.performance?.toLowerCase() === 'low'));
        }
    });
    return events;
};
// Icon helpers
export const getStatusIcon = (status) => {
    return ICONS.status[status?.toLowerCase()];
};
export const getChannelIcon = (channel) => ICONS.channel[channel?.toLowerCase()];
export const getPerfStyle = (group, level) => PERF_STYLES[group]?.[level] || { bg: '#fff', br: '#E4E7EC' };
// Calendar title formatter
export const formatCalendarTitle = (api) => {
    const { type } = api.view;
    const start = api.view.currentStart;
    const end = api.view.currentEnd;
    if (type === "dayGridMonth")
        return api.view.title;
    if (type === "dayGridWeek") {
        const endDate = new Date(end);
        endDate.setDate(endDate.getDate() - 1);
        return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${endDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
    }
    return start.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};
export const isPastMonth = (dateStr) => {
    const eventDate = new Date(dateStr);
    const today = new Date();
    const eventMonth = eventDate.getFullYear() * 12 + eventDate.getMonth();
    const currentMonth = today.getFullYear() * 12 + today.getMonth();
    return eventMonth < currentMonth;
};
// ✅ Convert object format to array format for API
export const transformAudiencesToArray = (audiences) => {
    if (!audiences)
        return { included: [], excluded: [] };
    const transform = (data) => {
        // Already array format - return as-is
        if (Array.isArray(data))
            return data;
        // Object format - convert to array
        if (data && typeof data === 'object') {
            return Object.entries(data).map(([id, value]) => ({
                id,
                name: value?.name || '',
                type: value?.type || 'segment'
            }));
        }
        return [];
    };
    return {
        included: transform(audiences.included),
        excluded: transform(audiences.excluded)
    };
};
